import { type ReactNode } from "react";

import { Button, Spinner, UserInitialAvatar } from "@/components/reusable";
import { DownloadMappedFileButton } from "@/components/history/DownloadMappedFileButton";

import { cn } from "@/lib/utils";

import { UNASSIGNED_PROVIDER_LABEL, type PipelineFileCardModel } from "@/lib/pipelineBoard";
import { resolveDqxPassFailCounts } from "@/lib/pipelineDqx";

import {
  ArrowRight,
  AlignJustify,
  CheckCircle2,
  Eye,
  Grid3x3,
  ShieldCheck,
  X,
  type LucideIcon,
} from "lucide-react";

import { PipelineStatusChip } from "./PipelineStatusChip";

function globalConfidenceColorClass(confidence: number): string {
  if (confidence >= 0.85) {
    return "text-emerald-700";
  }
  if (confidence >= 0.7) {
    return "text-amber-700";
  }
  return "text-red-700";
}

interface PipelineFileCardProps {
  card: PipelineFileCardModel;
  pendingAction?: "mapping" | "dqx" | "confirm" | "download";
  selected?: boolean;

  onToggleSelect?: (fileId: string) => void;

  onConfirmStructure?: (fileId: string) => void;

  onContinueMapping?: (fileId: string) => void;

  onPreview?: (fileId: string) => void;

  onRemove?: (fileId: string) => void;

  onRunDataQuality?: (fileId: string) => void;

  onViewDqxResults?: (fileId: string) => void;

  onDownloadMapped?: (fileId: string) => void | Promise<void>;
}

function CardActionLabel({
  icon: Icon,

  children,
}: {
  icon: LucideIcon;

  children: ReactNode;
}) {
  return (
    <span className="inline-flex min-w-0 items-center justify-center gap-1">
      <Icon className="h-3 w-3 shrink-0" strokeWidth={1.7} aria-hidden />

      <span className="truncate">{children}</span>
    </span>
  );
}

function ViewDqxResultsButton({
  fileId,
  isPreviewLoading,
  onViewDqxResults,
  className,
}: {
  fileId: string;
  isPreviewLoading: boolean;
  onViewDqxResults?: (fileId: string) => void;
  className?: string;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      className={cn(
        "rounded-full border border-red-600 bg-white text-red-700 shadow-none",
        "hover:border-red-700 hover:bg-red-50 hover:text-red-800",
        "focus-visible:ring-red-500",
        className
      )}
      onClick={() => onViewDqxResults?.(fileId)}
      disabled={isPreviewLoading}
      data-testid={`pipeline-view-dq-${fileId}`}
    >
      <CardActionLabel icon={Eye}>View DQ Results</CardActionLabel>
    </Button>
  );
}

function DqPassFailLine({
  rowsPassing,
  rowsWithIssues,
  totalRowsChecked,
}: {
  rowsPassing: number | null | undefined;
  rowsWithIssues: number | null | undefined;
  totalRowsChecked?: number | null;
}) {
  const resolved = resolveDqxPassFailCounts({
    rowsPassing,
    rowsWithIssues,
    totalRowsChecked,
  });
  if (resolved.rowsPassing == null && resolved.rowsWithIssues == null) return null;

  const passing = resolved.rowsPassing;
  const failing = resolved.rowsWithIssues ?? 0;
  // Only invent 0 passed when we could derive it from total − issues.
  if (passing == null) {
    return (
      <p
        className="mb-1 font-mono text-[9px] font-medium text-[var(--color-text-secondary)]"
        aria-label={`${failing.toLocaleString()} failed rows`}
      >
        <span className={failing > 0 ? "text-red-600" : "text-[var(--color-text-tertiary)]"}>
          {failing.toLocaleString()} failed rows
        </span>
      </p>
    );
  }

  return (
    <p
      className="mb-1 font-mono text-[9px] font-medium text-[var(--color-text-secondary)]"
      aria-label={`${passing.toLocaleString()} passed rows, ${failing.toLocaleString()} failed rows`}
    >
      <span className="text-emerald-700">{passing.toLocaleString()} passed rows</span>

      <span className="mx-1 text-[var(--color-text-tertiary)]">·</span>

      <span className={failing > 0 ? "text-red-600" : "text-[var(--color-text-tertiary)]"}>
        {failing.toLocaleString()} failed rows
      </span>
    </p>
  );
}

export function PipelineFileCard({
  card,
  pendingAction,
  selected = false,

  onToggleSelect,

  onConfirmStructure,

  onContinueMapping,

  onPreview,

  onRemove,

  onRunDataQuality,

  onViewDqxResults,

  onDownloadMapped,
}: PipelineFileCardProps) {
  const attention = card.statusVariant === "errors";

  const attentionAmber = card.statusVariant === "mapping" && !card.isMapped;

  const attentionValidate = card.stage === "validate";

  const attentionResolve = card.stage === "resolve" && (card.dqRowsWithIssues ?? 0) > 0;

  const attentionPublished = card.stage === "published";

  const showViewOnResolve = card.stage === "resolve" && card.canViewDqxResults;

  const showViewInlineValidate = card.stage === "validate" && card.canViewDqxResults;

  return (
    <article
      className={cn(
        "min-w-0 rounded-md border bg-[var(--color-bg-primary)] p-2 shadow-sm transition-[transform,box-shadow,border-color,background-color] hover:-translate-y-0.5 hover:shadow-md",

        attention && "border-red-200",

        attentionAmber && "border-amber-200",

        attentionValidate && "bg-teal-50/40 border-teal-200",

        attentionResolve && "border-red-200",

        attentionPublished && "border-emerald-200 bg-emerald-50/30"
      )}
      data-testid={`pipeline-card-${card.fileId}`}
    >
      <div className="mb-1 flex min-w-0 items-start justify-between gap-1.5">
        {onToggleSelect ? (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(card.fileId)}
            className="mt-0.5 h-3 w-3 shrink-0 accent-[var(--color-button-primary-bg)]"
            aria-label={`Select ${card.fileName}`}
            data-testid={`pipeline-select-${card.fileId}`}
          />
        ) : null}
        {card.uploadedBy ? (
          <UserInitialAvatar
            userId={card.uploadedBy.includes("@") ? null : card.uploadedBy}
            email={card.uploaderEmail ?? (card.uploadedBy.includes("@") ? card.uploadedBy : null)}
            className="mt-0.5"
          />
        ) : null}
        <p
          className="min-w-0 flex-1 font-mono text-[11px] font-semibold text-[var(--color-text-primary)] truncate"
          title={card.fileName}
        >
          {card.fileName}
        </p>

        {onRemove ? (
          <button
            type="button"
            className="shrink-0 rounded p-0.5 text-[var(--color-text-tertiary)] transition-colors hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
            onClick={() => onRemove(card.fileId)}
            disabled={card.isPreviewLoading}
            aria-label={`Remove ${card.fileName}`}
            data-testid={`pipeline-remove-${card.fileId}`}
          >
            <X className="h-3 w-3" strokeWidth={2} aria-hidden />
          </button>
        ) : null}
      </div>

      <div className="mb-1 flex flex-wrap items-center gap-1">
        <span
          className={cn(
            "max-w-full truncate rounded border px-1 py-0 text-[9px] font-semibold",
            card.providerLabel === UNASSIGNED_PROVIDER_LABEL
              ? "border-[var(--color-border-light)] bg-transparent text-[var(--color-text-tertiary)]"
              : "border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)]"
          )}
          title={`Provider: ${card.providerLabel}`}
          data-testid={`pipeline-provider-${card.fileId}`}
        >
          {`Provider: ${card.providerLabel}`}
        </span>
        <span
          className="max-w-full truncate rounded border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] px-1 py-0 text-[9px] font-semibold text-[var(--color-text-secondary)]"
          title={`Schema: ${card.schemaName}`}
        >
          {`Schema: ${card.schemaName}`}
        </span>

        {/* Fixed-height slot on its own line: counts arrive after the board
            renders, and filling reserved space must not change card height. */}
        <span className="flex h-3.5 basis-full items-center gap-x-1.5 font-mono text-[10px] font-medium text-[var(--color-text-tertiary)]">
          {card.tableCount != null ? (
            <span className="inline-flex items-center gap-0.5">
              <Grid3x3 className="h-2 w-2 shrink-0" strokeWidth={2} aria-hidden />
              {card.tableCount.toLocaleString()} {card.tableCount === 1 ? "table" : "tables"}
            </span>
          ) : null}

          {card.tableCount != null && card.rowCount != null ? <span aria-hidden>·</span> : null}

          {card.rowCount != null ? (
            <span className="inline-flex items-center gap-0.5">
              <AlignJustify className="h-2 w-2 shrink-0" strokeWidth={2} aria-hidden />
              {card.rowCount.toLocaleString()} rows
            </span>
          ) : null}
        </span>
      </div>

      {(card.stage === "resolve" || card.stage === "published") &&
      (card.dqRowsPassing != null || card.dqRowsWithIssues != null) ? (
        <DqPassFailLine
          rowsPassing={card.dqRowsPassing}
          rowsWithIssues={card.dqRowsWithIssues}
          totalRowsChecked={card.dqTotalRowsChecked}
        />
      ) : null}

      <div className="mb-1 flex min-w-0 flex-wrap items-center gap-1">
        <PipelineStatusChip label={card.statusLabel} variant={card.statusVariant} />

        {card.stage === "upload" && card.globalConfidence != null ? (
          <span
            className={cn(
              "inline-flex items-center rounded border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] px-1 py-0 font-mono text-[9px] font-semibold",
              globalConfidenceColorClass(card.globalConfidence),
              card.requiresReview ? "border-red-200 bg-red-50" : null
            )}
            title={
              card.requiresReview
                ? "Ingestion confidence below review threshold — open Preview to confirm"
                : "Ingestion confidence"
            }
            data-testid={`pipeline-confidence-${card.fileId}`}
          >
            {`${Math.round(card.globalConfidence * 100)}% confidence`}
          </span>
        ) : null}

        {showViewInlineValidate ? (
          <ViewDqxResultsButton
            fileId={card.fileId}
            isPreviewLoading={card.isPreviewLoading}
            onViewDqxResults={onViewDqxResults}
            className="h-5 min-w-0 shrink-0 px-2 text-[9px]"
          />
        ) : null}
      </div>

      {card.detail &&
      (card.canViewDqxResults || (!card.canViewDqxResults && card.stage === "validate")) ? (
        <p className="mb-1 text-[9px] text-[var(--color-text-tertiary)] line-clamp-2">
          {card.detail}
        </p>
      ) : null}

      {card.canConfirmStructure ||
      card.canContinueMapping ||
      card.canRunDataQuality ||
      card.isDqxRunning ||
      showViewOnResolve ||
      (card.stage === "upload" &&
        card.extractionCategory === "succeeded" &&
        !card.canConfirmStructure) ? (
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-1">
            {card.canConfirmStructure ? (
              <Button
                type="button"
                size="sm"
                className="h-6 min-w-0 flex-1 truncate px-1.5 text-[10px]"
                onClick={() => onConfirmStructure?.(card.fileId)}
                disabled={card.isPreviewLoading || pendingAction != null}
                aria-busy={pendingAction === "confirm" || card.isPreviewLoading}
                data-testid={`pipeline-confirm-structure-${card.fileId}`}
              >
                {card.isPreviewLoading || pendingAction === "confirm" ? (
                  <span className="flex items-center gap-1.5">
                    <Spinner size="sm" />
                    {pendingAction === "confirm" ? "Confirming…" : "Loading…"}
                  </span>
                ) : (
                  <CardActionLabel icon={ArrowRight}>Confirm structure</CardActionLabel>
                )}
              </Button>
            ) : null}

            {card.canContinueMapping ? (
              <Button
                type="button"
                size="sm"
                className="h-6 min-w-0 flex-1 truncate px-1.5 text-[10px]"
                onClick={() => onContinueMapping?.(card.fileId)}
                disabled={card.isPreviewLoading || pendingAction != null}
                aria-busy={pendingAction === "mapping"}
              >
                {pendingAction === "mapping" ? (
                  <span className="flex items-center gap-1.5">
                    <Spinner size="sm" />
                    Opening mapping…
                  </span>
                ) : (
                  <CardActionLabel icon={Grid3x3}>Start mapping</CardActionLabel>
                )}
              </Button>
            ) : null}

            {card.canRunDataQuality ? (
              <Button
                type="button"
                size="sm"
                className="h-6 min-w-0 flex-1 truncate px-1.5 text-[10px]"
                onClick={() => onRunDataQuality?.(card.fileId)}
                disabled={card.isPreviewLoading || pendingAction != null}
                aria-busy={pendingAction === "dqx"}
                data-testid={`pipeline-run-dq-${card.fileId}`}
              >
                {pendingAction === "dqx" ? (
                  <span className="flex items-center gap-1.5">
                    <Spinner size="sm" />
                    Starting DQ…
                  </span>
                ) : (
                  <CardActionLabel icon={ShieldCheck}>Run Data Quality Checks</CardActionLabel>
                )}
              </Button>
            ) : null}

            {card.isDqxRunning ? (
              <Button
                type="button"
                size="sm"
                className="h-6 min-w-0 flex-1 truncate px-1.5 text-[10px]"
                disabled
                data-testid={`pipeline-validating-${card.fileId}`}
              >
                <span className="flex items-center gap-1.5">
                  <Spinner size="sm" />

                  {card.statusLabel === "Retrieving DQ results…" ? "Retrieving…" : "Validating…"}
                </span>
              </Button>
            ) : null}

            {card.stage === "upload" &&
            card.extractionCategory === "succeeded" &&
            !card.canConfirmStructure ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-6 w-7 px-0"
                onClick={() => onPreview?.(card.fileId)}
                aria-label="Preview"
              >
                <Eye className="h-3 w-3" strokeWidth={1.6} aria-hidden />
              </Button>
            ) : null}
          </div>

          {showViewOnResolve ? (
            <ViewDqxResultsButton
              fileId={card.fileId}
              isPreviewLoading={card.isPreviewLoading}
              onViewDqxResults={onViewDqxResults}
              className="h-6 min-w-0 w-full truncate px-2 text-[10px]"
            />
          ) : null}
        </div>
      ) : null}

      {card.stage === "published" && onDownloadMapped ? (
        <DownloadMappedFileButton
          fileId={card.fileId}
          fallbackFileName={card.fileName}
          onDownload={onDownloadMapped}
          pending={pendingAction === "download"}
          disabled={pendingAction != null}
          preparingLabel="Preparing…"
          label="Download mapped file"
          data-testid={`pipeline-download-mapped-${card.fileId}`}
          className={cn(
            "h-6 min-w-0 w-full truncate px-2 text-[10px]",
            "rounded-full border border-emerald-600 bg-white text-emerald-700 shadow-none",
            "hover:border-emerald-700 hover:bg-emerald-50 hover:text-emerald-800",
            "focus-visible:ring-emerald-500"
          )}
        />
      ) : null}

      {card.uploadPhase ? (
        <div
          className="mt-1 flex items-center justify-end"
          data-testid={`pipeline-upload-indicator-${card.fileId}`}
        >
          {card.uploadPhase === "uploading" ? (
            <span role="status" aria-label={`Processing ${card.fileName}`}>
              <Spinner size="sm" />
            </span>
          ) : (
            <CheckCircle2
              className="h-3.5 w-3.5 text-emerald-600"
              strokeWidth={2}
              aria-label={`${card.fileName} processed`}
            />
          )}
        </div>
      ) : null}
    </article>
  );
}
